import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { PenLine, Send, Copy, X, FileDown, ShieldCheck } from 'lucide-react';

const STATUS_STYLE = {
  pending: 'bg-amber-100 text-amber-700',
  signed: 'bg-green-100 text-green-700',
  revoked: 'bg-gray-100 text-gray-500',
  expired: 'bg-red-100 text-red-600',
};

const SupervisorSignOff = () => {
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ supervisor_name: '', supervisor_email: '', message: '' });
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/internship-portal/signature-requests`);
      setRequests(res.data.requests || []);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!form.supervisor_name.trim() || !form.supervisor_email.includes('@')) {
      toast.error("Enter your supervisor's name and a valid email."); return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/internship-portal/signature-requests`, {
        ...form, base_url: window.location.origin,
      });
      toast.success('Signature request sent to your supervisor.');
      setOpen(false); setForm({ supervisor_name: '', supervisor_email: '', message: '' });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send request.'); }
    finally { setSending(false); }
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/sign-hours/${token}`;
    navigator.clipboard?.writeText(link);
    toast.success('Signing link copied.');
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this pending request? The link will stop working.')) return;
    try { await axios.post(`${API_URL}/internship-portal/signature-requests/${id}/revoke`); await load(); toast.success('Request revoked.'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not revoke.'); }
  };

  const downloadSigned = async (token) => {
    try {
      const res = await axios.get(`${API_URL}/internship-portal/sign/${token}/report.pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'signed_internship_hours_report.pdf';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download the signed report.'); }
  };

  return (
    <div className="bg-white rounded-2xl border p-6" data-testid="supervisor-signoff">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="font-serif text-lg font-bold text-primary flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-secondary" /> Supervisor Sign-Off</h3>
        <Button size="sm" className="bg-primary text-white" onClick={() => setOpen(true)} data-testid="request-signature-btn"><PenLine className="w-4 h-4 mr-1" /> Request Signature</Button>
      </div>
      <p className="text-sm text-gray-500 mb-4">Send your supervisor a secure, one-time link to review and digitally sign your hour report. A snapshot is frozen when you send it — later edits won't change what they signed. You can revoke a pending request anytime.</p>

      {requests.length === 0 ? (
        <p className="text-gray-400 text-sm py-4 text-center">No signature requests yet.</p>
      ) : (
        <div className="space-y-2">
          {requests.map((r) => (
            <div key={r.request_id} className="flex flex-wrap items-center justify-between gap-2 border rounded-xl p-3" data-testid={`sig-row-${r.request_id}`}>
              <div>
                <p className="text-sm font-medium text-primary">{r.supervisor_name} <span className="text-gray-400 font-normal">· {r.supervisor_email}</span></p>
                <p className="text-xs text-gray-400">Sent {String(r.created_at).slice(0, 10)}{r.signed_at ? ` · Signed ${String(r.signed_at).slice(0, 10)} by ${r.signer_name || ''}` : ''}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={STATUS_STYLE[r.status] || 'bg-gray-100 text-gray-500'} data-testid={`sig-status-${r.request_id}`}>{r.status}</Badge>
                {r.status === 'pending' && <>
                  <button className="text-gray-400 hover:text-primary p-1" title="Copy link" onClick={() => copyLink(r.token)} data-testid={`sig-copy-${r.request_id}`}><Copy className="w-4 h-4" /></button>
                  <button className="text-gray-400 hover:text-red-600 p-1" title="Revoke" onClick={() => revoke(r.request_id)} data-testid={`sig-revoke-${r.request_id}`}><X className="w-4 h-4" /></button>
                </>}
                {r.status === 'signed' && <button className="text-secondary hover:text-primary p-1 flex items-center gap-1 text-sm" onClick={() => downloadSigned(r.token)} data-testid={`sig-download-${r.request_id}`}><FileDown className="w-4 h-4" /> Signed PDF</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle>Request Supervisor Signature</DialogTitle>
            <DialogDescription>Email your supervisor a secure one-time link to review and sign your hour report.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-gray-500 mb-1 block">Supervisor Name</Label><Input value={form.supervisor_name} onChange={(e) => setForm({ ...form, supervisor_name: e.target.value })} placeholder="e.g., Jane Doe, LCSW-S" data-testid="sig-supervisor-name" /></div>
            <div><Label className="text-xs text-gray-500 mb-1 block">Supervisor Email</Label><Input type="email" value={form.supervisor_email} onChange={(e) => setForm({ ...form, supervisor_email: e.target.value })} placeholder="supervisor@agency.org" data-testid="sig-supervisor-email" /></div>
            <div><Label className="text-xs text-gray-500 mb-1 block">Message (optional)</Label><Textarea rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="A short note to your supervisor" data-testid="sig-message" /></div>
            <p className="text-xs text-gray-400">We'll email your supervisor a secure link that expires in 14 days. They see only this hour report — nothing else in your account.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-primary text-white" onClick={send} disabled={sending} data-testid="sig-send-btn"><Send className="w-4 h-4 mr-1" /> {sending ? 'Sending…' : 'Send Request'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupervisorSignOff;
